import { skillScannerCopy } from "../skill-scanner-copy.js";
import { importWithChunkReload } from "./chunk-reload";
import { installSingleOpenFaqAccordion } from "./faq-accordion";
import type { ScannerIntentState } from "./scanner-intent-bootstrap";
import { installScannerIntentBootstrap } from "./scanner-intent-bootstrap";

declare global {
  interface Window {
    __SKILLSECURITY_SKILL_SCANNER_HYDRATED__?: boolean;
    __SKILLSECURITY_SKILL_SCANNER_INITIAL_STATE__?: ScannerIntentState;
  }
}

const rootElement = document.querySelector<HTMLElement>("#skill-scanner-root");
let cleanupScanner = (): void => undefined;

if (rootElement !== null) {
  const setSubmitIntentPending = (pending: boolean): void => {
    rootElement.toggleAttribute("data-scan-intent-pending", pending);
  };
  const scannerBootstrap = installScannerIntentBootstrap({
    selectors: {
      scanner: ".skill-scanner-workspace",
      input: "#skill-scanner-root textarea",
      submit: "#skill-scanner-root button.primary-action",
      uploadAction: "#skill-scanner-root .workbench-add-files",
      fileInput: "#skill-scanner-source-file",
      uploadTargets: "#skill-scanner-root .workbench-add-files, #skill-scanner-source-file",
    },
    readState: () => window.__SKILLSECURITY_SKILL_SCANNER_INITIAL_STATE__,
    writeState: (state) => {
      window.__SKILLSECURITY_SKILL_SCANNER_INITIAL_STATE__ = state;
    },
    isHydrated: () => window.__SKILLSECURITY_SKILL_SCANNER_HYDRATED__ === true,
    markHydrated: () => {
      window.__SKILLSECURITY_SKILL_SCANNER_HYDRATED__ = true;
    },
    onSubmitIntentPendingChange: setSubmitIntentPending,
    loadMount: async () => {
      const { mountContentPageScanner } = await importWithChunkReload(
        () => import("./content-page-scanner"),
      );
      return () => {
        mountContentPageScanner(rootElement, {
          locale: "en",
          scannerCopy: skillScannerCopy.scanner,
          initialState: window.__SKILLSECURITY_SKILL_SCANNER_INITIAL_STATE__,
          initialTextareaValue: rootElement.querySelector("textarea")?.value ?? "",
          onInitialStateConsumed: () => {
            delete window.__SKILLSECURITY_SKILL_SCANNER_INITIAL_STATE__;
          },
          fileInputId: "skill-scanner-source-file",
          layoutClassName: "skill-scanner-layout",
          stateClassName: "skill-scanner-state",
          initialEntrance: false,
        });
      };
    },
  });
  cleanupScanner = () => scannerBootstrap.dispose();
}

const cleanupFaq = installSingleOpenFaqAccordion({
  summarySelector: ".skill-scanner-faq-list summary",
  listSelector: ".skill-scanner-faq-list",
});
let cleanupMotion = (): void => undefined;
let cleanupReportFocus = (): void => undefined;
let cleanupReportObserver = (): void => undefined;
let pageDisposed = false;

async function initializeStaticEnhancements(): Promise<void> {
  const reportRoot = document.querySelector<HTMLElement>("#skill-scanner-report-root");
  const motionModule = await importWithChunkReload(() => import("./skill-scanner-motion"));
  if (pageDisposed) {
    return;
  }
  cleanupMotion = motionModule.installSkillScannerMotion();
  if (reportRoot === null) {
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }
      observer.disconnect();
      void importWithChunkReload(() => import("./skill-scanner-report")).then(
        async (reportModule) => {
          if (pageDisposed) {
            return;
          }
          reportModule.mountSkillScannerReport(reportRoot);
          await nextAnimationFrame();
          if (pageDisposed) {
            return;
          }
          cleanupReportFocus = motionModule.installSkillScannerReportFocus();
        },
      );
    },
    { rootMargin: "0px" },
  );
  cleanupReportObserver = () => observer.disconnect();
  observer.observe(reportRoot);
}

void initializeStaticEnhancements();

function nextAnimationFrame(): Promise<void> {
  return new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
}

function handlePageHide(event: PageTransitionEvent): void {
  if (event.persisted) {
    return;
  }

  pageDisposed = true;
  window.removeEventListener("pagehide", handlePageHide);
  cleanupScanner();
  cleanupMotion();
  cleanupReportObserver();
  cleanupReportFocus();
  cleanupFaq();
}

window.addEventListener("pagehide", handlePageHide);

export {};
